import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Building2, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Spinner, EmptyState } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PLAN_LABELS = { starter: "Starter", standard: "Standard", premium: "Premium" };

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCentral = ["principal_admin", "finance_agent"].includes(user?.role);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", contact_person: "", plan: "starter" });

  const load = () => api.get("/customers").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success("Client cree");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", city: "", contact_person: "", plan: "starter" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!items) return <Spinner />;
  const filtered = items.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.city || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader
        title="Clients & groupes scolaires"
        subtitle="Comptes clients avec administration independante et isolation stricte des donnees."
        actions={isCentral && (
          <Button data-testid="add-customer-btn" onClick={() => setOpen(true)} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4 mr-1.5" /> Nouveau client
          </Button>
        )}
      />

      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="customer-search" placeholder="Rechercher un client ou une ville..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
        </div>
        {filtered.length === 0 ? (
          <EmptyState icon={Building2} title="Aucun client" hint="Creez votre premier groupe scolaire." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="py-4 px-6">Groupe scolaire</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Formule</TableHead>
                <TableHead className="text-center">Etablissements</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`table-row-${c.id}`} className="cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>
                  <TableCell className="py-4 px-6">
                    <p className="font-semibold text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-400">{c.email}</p>
                  </TableCell>
                  <TableCell className="text-slate-600">{c.city}</TableCell>
                  <TableCell className="text-slate-600">{c.contact_person || "-"}</TableCell>
                  <TableCell><span className="rounded-md bg-slate-900 px-2 py-0.5 text-xs font-semibold text-white">{PLAN_LABELS[c.plan]}</span></TableCell>
                  <TableCell className="text-center font-mono font-semibold">{c.establishment_count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Nouveau client / groupe scolaire</DialogTitle>
          <DialogDescription>Enregistrez un nouveau groupe scolaire client avec sa formule d'abonnement.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom du groupe</Label><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input data-testid="customer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Telephone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Personne contact</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div>
              <Label>Formule</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger data-testid="customer-plan" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="customer-save" onClick={create} disabled={saving || !form.name || !form.email} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Creer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
